/**
 * 登录成功后的新手跳转（仅新用户）：
 *
 * 新用户判定：福利金未领取（且无 TRIAL 余额），或本机已进入 1/2/3 引导计数。
 * 老用户（已领过福利金且从未进入本机引导）→ 软回跳，不强制。
 *
 * 第 1 次登录 → 1+1=? 模拟盘
 * 第 2 次登录 → cryptoPage(5M) 返回的 BTC 5 分钟涨跌
 * 第 3 次登录 → 首页
 * 第 4 次及以后 → 软回跳（引导结束）
 *
 * 有 requiresAuth 回跳（交易/持仓等）时优先软回跳，不打断用户意图。
 */

import { buildCryptoUpdownDetailRoute } from '@/composables/useCryptoUpdownDetailNavigation.js';
import { TRIAL_FUND_SIM_ROUTE_NAME } from '@/constants/trial-fund.js';
import { CRYPTO_FEED_PAGE_SIZE } from '@/domain/crypto/crypto-feed-adapter.js';
import {
  normalizeCryptoMinuteUpdownFeedRows,
  parseMinuteUpdownSlug,
  resolveUpdownRoundSlugSource,
} from '@/domain/crypto/crypto-minute-slug.js';
import { resolveCollectionSlug } from '@/domain/market/collection-slug.js';
import { useTrialFundStore } from '@/stores/trialFund.js';
import { softNavigateAfterAuth } from '@/utils/auth-page-refresh.js';
import {
  resolveHasTrialFund,
  tryTrackAuthRegisterOnce,
} from '@/utils/analytics-auth-register.js';
import { trackPostLoginOnboarding } from '@/utils/analytics-conversion-extras.js';
import { getUserId } from '@/utils/auth-token.js';
import { CRYPTO_PRICE_SLUG_MAP } from '@/utils/crypto-sidebar-map.js';
import { getCryptoCollectionPage } from '@/api/market.js';
import { getUserProfile } from '@/api/user.js';

const BTC_UPDOWN_5M_SERIES_KEY = 'btc-updown-5m';
const LOGIN_COUNT_KEY_PREFIX = 'ds-post-login-count:';
/** 兼容旧版仅写过 5m 引导标记的账号 */
const UPDOWN_GUIDED_KEY_PREFIX = 'ds-post-login-updown-guided:';

function storageKey(prefix, userId) {
  const uid = String(userId ?? '').trim();
  return uid ? `${prefix}${uid}` : '';
}

function readLoginCount(userId) {
  const key = storageKey(LOGIN_COUNT_KEY_PREFIX, userId);
  if (!key || typeof localStorage === 'undefined') return 0;
  try {
    const n = Number(localStorage.getItem(key));
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

function writeLoginCount(userId, count) {
  const key = storageKey(LOGIN_COUNT_KEY_PREFIX, userId);
  if (!key || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, String(Math.max(0, Math.floor(Number(count) || 0))));
  } catch {
    /* ignore quota */
  }
}

/** 旧标记迁移：曾做过 5m 引导 → 至少记为已登录 2 次 */
function migrateLegacyFlags(userId) {
  const upKey = storageKey(UPDOWN_GUIDED_KEY_PREFIX, userId);
  if (!upKey || typeof localStorage === 'undefined') return;
  try {
    if (localStorage.getItem(upKey) === '1' && readLoginCount(userId) < 2) {
      writeLoginCount(userId, 2);
    }
  } catch {
    /* ignore */
  }
}

/** @returns {number} 本次登录后的累计次数（从 1 起） */
function bumpPostLoginCount(userId) {
  migrateLegacyFlags(userId);
  const next = readLoginCount(userId) + 1;
  writeLoginCount(userId, next);
  return next;
}

/**
 * 是否走新用户 1/2/3 引导：
 * - 未领福利金 → 新用户
 * - 或本机已有引导计数（漏斗进行中 / 刚领完仍要走完 2、3 次）
 */
function isNewUserOnboardingEligible(hasTrialFund, userId) {
  const prevCount = readLoginCount(userId);
  if (prevCount > 0) return true;
  return !hasTrialFund;
}

/**
 * @param {import('vue-router').Router} router
 * @param {string|null|undefined} redirect
 */
function isIntentionalAuthReturn(router, redirect) {
  const target = String(redirect ?? '').trim();
  if (!target || !router) return false;
  if (target === '/' || target === '/home') return false;
  try {
    const resolved = router.resolve(target);
    return Boolean(resolved?.meta?.requiresAuth);
  } catch {
    return false;
  }
}

function pushRoute(router, location) {
  if (!router || !location) return Promise.resolve();
  return router.push(location).catch((err) => {
    console.warn('[auth] post-login onboarding navigate failed', err);
  });
}

/** 与 Crypto Feed 同源：cryptoPage(5M) 取 btc-updown-5m，live 纠偏交给 beforeEnter */
async function goUpdown5m(router) {
  try {
    const data = await getCryptoCollectionPage(
      { priceSlug: CRYPTO_PRICE_SLUG_MAP.m5 },
      { pageNum: 1, pageSize: CRYPTO_FEED_PAGE_SIZE }
    );
    const rows = normalizeCryptoMinuteUpdownFeedRows(
      Array.isArray(data?.rows) ? data.rows : []
    );
    const hit = rows.find((row) => {
      const slug = resolveUpdownRoundSlugSource(row) || resolveCollectionSlug(row);
      return parseMinuteUpdownSlug(slug)?.seriesKey === BTC_UPDOWN_5M_SERIES_KEY;
    });
    const id = String(hit?.id ?? hit?.collectionId ?? '').trim();
    if (id) {
      const slug = resolveUpdownRoundSlugSource(hit) || resolveCollectionSlug(hit) || '';
      const route = buildCryptoUpdownDetailRoute({
        id,
        seriesKey: BTC_UPDOWN_5M_SERIES_KEY,
        slug,
      });
      if (route) {
        await pushRoute(router, route);
        return 'updown5m';
      }
    }
  } catch (err) {
    console.warn('[auth] post-login cryptoPage btc-updown-5m failed', err);
  }
  await pushRoute(router, { name: 'Crypto' });
  return 'updown5m';
}

/**
 * @param {import('vue-router').Router} router
 * @param {string|null|undefined} softRedirect
 * @param {{ quietShell?: boolean }} [opts]
 * @returns {Promise<'trialSim' | 'updown5m' | 'home' | 'soft' | 'skip'>}
 */
export async function navigatePostLoginOnboarding(router, softRedirect, { quietShell = false } = {}) {
  if (!router) return 'skip';

  if (quietShell || isIntentionalAuthReturn(router, softRedirect)) {
    softNavigateAfterAuth(router, softRedirect);
    return softRedirect ? 'soft' : 'skip';
  }

  const trialFundStore = useTrialFundStore();
  let profile = null;
  try {
    const [profileResult] = await Promise.all([
      getUserProfile().catch((err) => {
        console.warn('[auth] post-login getUserProfile failed', err);
        return null;
      }),
      trialFundStore.refreshConditions(),
    ]);
    profile = profileResult;
  } catch (err) {
    console.warn('[auth] post-login trial conditions failed, soft redirect', err);
    softNavigateAfterAuth(router, softRedirect);
    return 'soft';
  }

  const userId = getUserId();
  const claimed = Boolean(trialFundStore.conditions?.claimed);
  const hasTrialFund = resolveHasTrialFund(claimed);

  // 新用户注册埋点：注册时间窗口内 + 未领试用金（进试玩盘前打，避免漏报）
  tryTrackAuthRegisterOnce({
    createTime: profile?.createTime,
    hasTrialFund,
  });

  // 老用户：已领福利金且从未进过本机引导 → 不强制跳转
  if (!isNewUserOnboardingEligible(hasTrialFund, userId)) {
    softNavigateAfterAuth(router, softRedirect);
    return 'soft';
  }

  const loginCount = bumpPostLoginCount(userId);

  // 第 1 次：新手盘（若中途已领取则直接 5m）
  if (loginCount <= 1) {
    if (!hasTrialFund) {
      trialFundStore.$patch({ loggedView: 'closed' });
      await pushRoute(router, { name: TRIAL_FUND_SIM_ROUTE_NAME });
      trackPostLoginOnboarding('trialSim', { loginCount });
      return 'trialSim';
    }
    const kind = await goUpdown5m(router);
    trackPostLoginOnboarding(kind, { loginCount });
    return kind;
  }

  // 第 2 次：5 分钟涨跌
  if (loginCount === 2) {
    const kind = await goUpdown5m(router);
    trackPostLoginOnboarding(kind, { loginCount });
    return kind;
  }

  // 第 3 次：首页
  if (loginCount === 3) {
    await pushRoute(router, { name: 'Home' });
    trackPostLoginOnboarding('home', { loginCount });
    return 'home';
  }

  // 引导结束后：软回跳，不再每次踢回首页
  softNavigateAfterAuth(router, softRedirect);
  return 'soft';
}
